import React, { useState } from 'react';
import { AppView } from '../App';
import Logo from './Logo';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

interface HeaderProps {
  activeView: AppView;
  onViewChange: (view: AppView) => void;
}

export default function Header({ activeView, onViewChange }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (activeView !== 'landing') {
      onViewChange('landing');
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 300);
    } else {
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#fcfcfd]/90 border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo Brand Group */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => { setIsMobileMenuOpen(false); onViewChange('landing'); }}>
            <Logo className="h-8 md:h-10 w-auto" />
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-8">
            <a href="#kurumsal" onClick={(e) => handleNavClick(e, 'kurumsal')} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
              Hakkımızda
            </a>
            <a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
              Servislerimiz
            </a>
            <a href="#omniops" onClick={(e) => handleNavClick(e, 'omniops')} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1">
              OmniOps®
            </a>
            <button 
              className={`text-sm font-medium transition-colors ${activeView === 'sandbox' ? 'text-[#E30613]' : 'text-slate-600 hover:text-slate-900'}`}
              onClick={() => onViewChange('sandbox')}
            >
              Etkileşimli Tur
            </button>
            <a href="#iletisim" onClick={(e) => handleNavClick(e, 'iletisim')} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
              İletişim
            </a>
          </nav>

          {/* Action CallButtons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="#teklif"
              onClick={(e) => handleNavClick(e, 'teklif')}
              className="bg-[#E30613] hover:bg-red-700 text-white px-6 py-3 md:px-5 md:py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-lg shadow-red-500/20 block"
            >
              Teklif Alın
            </a>
          </div>

          {/* Mobile Menu Toggle Button */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-3 text-slate-600 hover:text-slate-900 focus:outline-none min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Menüyü Aç/Kapat"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b border-slate-200 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-1 shadow-inner">
              <a href="#kurumsal" onClick={(e) => handleNavClick(e, 'kurumsal')} className="block px-4 py-4 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-xl min-h-[44px]">
                Hakkımızda
              </a>
              <a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="block px-4 py-4 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-xl min-h-[44px]">
                Servislerimiz
              </a>
              <a href="#omniops" onClick={(e) => handleNavClick(e, 'omniops')} className="block px-4 py-4 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-xl min-h-[44px]">
                OmniOps®
              </a>
              <button 
                className={`w-full text-left block px-4 py-4 text-base font-medium rounded-xl min-h-[44px] ${activeView === 'sandbox' ? 'text-[#E30613] bg-red-50' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'}`}
                onClick={() => { setIsMobileMenuOpen(false); onViewChange('sandbox'); }}
              >
                Etkileşimli Tur
              </button>
              <a href="#iletisim" onClick={(e) => handleNavClick(e, 'iletisim')} className="block px-4 py-4 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-xl min-h-[44px]">
                İletişim
              </a>
              <div className="pt-4 px-2">
                <a
                  href="#teklif"
                  onClick={(e) => handleNavClick(e, 'teklif')}
                  className="w-full text-center bg-[#E30613] hover:bg-red-700 text-white px-6 py-4 rounded-xl text-base font-bold transition-colors shadow-lg shadow-red-500/20 block min-h-[44px]"
                >
                  Altyapı Teklifi Alın
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
